/**
 * The status the entrance displays.
 *
 * The reference artwork has "SYSTEM ONLINE" and "ALL SYSTEMS NOMINAL" printed
 * on the bulkhead. Printing them would be a fabricated production value — the
 * one thing the design mandate forbids — so they are wired to the health
 * endpoint instead and say whatever it actually reports.
 *
 * That endpoint is unauthenticated by design: it is what a load balancer and an
 * uptime monitor call, and it discloses nothing. It is therefore readable from
 * a locked door, which is exactly where this is needed.
 */
import { useEffect, useState } from 'react';

export type SystemStatus = 'CHECKING' | 'ONLINE' | 'DEGRADED' | 'UNREACHABLE';

export interface SystemState {
  status: SystemStatus;
  /** Short, upper-case, for the top strip. */
  label: string;
  /** The longer line for the lower strip. */
  detail: string;
  /** The host this Bridge is actually being served from. */
  host: string;
}

const OFFLINE: Omit<SystemState, 'host'> = {
  status: 'UNREACHABLE',
  label: 'SYSTEM UNREACHABLE',
  detail: 'The Bridge cannot reach its own API',
};

export function useSystemStatus(): SystemState {
  const [state, setState] = useState<Omit<SystemState, 'host'>>({
    status: 'CHECKING',
    label: 'CHECKING SYSTEMS',
    detail: 'Asking the Bridge how it is',
  });

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        // Readiness rather than liveness: a process that is up but cannot read
        // its database is not "all systems nominal", and saying so would be the
        // fabrication this exists to avoid.
        const response = await fetch('/api/v1/ready', { headers: { Accept: 'application/json' } });
        if (cancelled) return;
        const body = (await response.json()) as {
          status?: string;
          checks?: { name: string; ok: boolean; detail: string }[];
        };
        const failed = (body.checks ?? []).filter((check) => !check.ok);
        if (response.ok && failed.length === 0) {
          setState({
            status: 'ONLINE',
            label: 'SYSTEM ONLINE',
            detail: 'All systems nominal',
          });
          return;
        }
        setState({
          status: 'DEGRADED',
          label: 'SYSTEM DEGRADED',
          // Name what is wrong. "Degraded" on its own tells nobody anything.
          detail: failed[0]?.detail ?? 'One or more systems are not ready',
        });
      } catch {
        if (!cancelled) setState(OFFLINE);
      }
    };

    void check();
    // Slow on purpose. This is a status light on a door, not a monitor.
    const handle = window.setInterval(() => void check(), 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  return { ...state, host: window.location.host };
}
